import React from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { type TemplateOption, nudgeNoteTypesFor, useTemplateOptions } from "../../hooks/useTemplateOptions";
import { getRecentNoteTypes } from "../../service/notes.service";
import { type SchedulePick, type ScheduleSlot, useGetTodaySchedulePickQuery } from "../../service/schedulePick.service";
import { cacheClients } from "../../slices/clientSlice";
import { EventType, trackEvent } from "../../utils/analytics";
import { eventEmitter } from "../../utils/eventEmitter";
import { readStoredTemplateChoice, rememberTemplateChoice, resolveSessionTemplate } from "../../utils/sessionTemplate";
import { type QuickStartView, loadDismissed, quickStartView, saveDismissed, upcomingSlots } from "./quickStart";

// The slot chip and "who is next" drift with the clock; the sheet itself only changes when a
// note lands (refetched on that event) or a pull runs.
const SCHEDULE_POLL_MS = 5 * 60 * 1000;
// Coming back to the tab refetches the template list, at most this often.
const TEMPLATE_FOCUS_REFRESH_MS = 30 * 1000;

export type QuickStartSchedule = {
  /** The card asks for today's sheet (everywhere but the demo). */
  enabled: boolean;
  pick: SchedulePick | null;
  /** What the card offers; see ``quickStartView``. */
  view: QuickStartView;
  /** Every undocumented appointment left today, in the server's order (See all). */
  upcoming: ScheduleSlot[];
  /** Put a patient from See all on the card. */
  choose: (clientId: number) => void;
  /**
   * × : turn the schedule's pick down (Start Encounter until the schedule moves on). On a
   * patient the clinician picked from See all it only undoes that pick.
   */
  dismiss: () => void;
  /** Undo the ×. */
  restore: () => void;
};

/**
 * Today's sheet for the Start card. Reads the same ranked sheet as the Start Encounter
 * drawer (``GET /client/schedule/today/``), so the card and the drawer agree on who is
 * next; one subscription is shared by every mounted card.
 */
export const useQuickStartSchedule = (): QuickStartSchedule => {
  const dispatch = useAppDispatch();
  const { isDemoPath } = useAppSelector((state) => state.appState);
  // The card only mounts for accounts a pulled schedule reaches (``has_ehr_schedule``).
  const enabled = !isDemoPath;

  const { data, refetch } = useGetTodaySchedulePickQuery(
    {},
    { skip: !enabled, pollingInterval: SCHEDULE_POLL_MS, refetchOnFocus: true, refetchOnMountOrArgChange: true },
  );
  const pick = enabled ? data ?? null : null;

  const [chosenId, setChosenId] = React.useState<number | null>(null);
  const [dismissedId, setDismissedId] = React.useState<number | null>(null);

  React.useEffect(() => {
    setDismissedId(loadDismissed(pick?.day));
  }, [pick?.day]);

  // Rows carry full client objects; caching them lets the recording header and the
  // client page resolve the patient without another fetch.
  React.useEffect(() => {
    if (!pick) return;
    const clients = upcomingSlots(pick).map((slot) => slot.client);
    if (clients.length) dispatch(cacheClients(clients));
  }, [pick, dispatch]);

  // A note that starts or lands moves its patient to "documented": re-rank right away.
  React.useEffect(() => {
    if (!enabled) return;
    const onNotesChanged = () => {
      refetch();
    };
    eventEmitter.on("noteCreated", onNotesChanged);
    eventEmitter.on("refreshNotes", onNotesChanged);
    return () => {
      eventEmitter.off("noteCreated", onNotesChanged);
      eventEmitter.off("refreshNotes", onNotesChanged);
    };
  }, [enabled, refetch]);

  const upcoming = React.useMemo(() => upcomingSlots(pick), [pick]);
  const view = React.useMemo(() => quickStartView(pick, { chosenId, dismissedId }), [pick, chosenId, dismissedId]);

  const choose = React.useCallback(
    (clientId: number) => {
      const rank = upcoming.findIndex((slot) => slot.client.id === clientId);
      trackEvent(EventType.QUICK_START_PATIENT_SWITCHED, {
        from_view: view.kind,
        to_rank: rank >= 0 ? rank + 1 : null,
        was_suggestion: pick?.suggestion?.client_id === clientId,
      });
      setChosenId(clientId);
    },
    [upcoming, view.kind, pick?.suggestion?.client_id],
  );

  const dismiss = React.useCallback(() => {
    if (view.kind === "quick" && view.chosen) {
      trackEvent(EventType.QUICK_START_DISMISSED, { view: "chosen", candidates: 1, confidence: null });
      setChosenId(null);
      return;
    }
    const id = upcoming[0]?.client.id;
    if (id == null || !pick) return;
    trackEvent(EventType.QUICK_START_DISMISSED, {
      view: view.kind,
      candidates: view.kind === "choose" ? view.slots.length + view.more : 1,
      confidence: pick.suggestion?.confidence ?? null,
    });
    saveDismissed(pick.day, id);
    setDismissedId(id);
    setChosenId(null);
  }, [upcoming, pick, view]);

  const restore = React.useCallback(() => {
    if (pick) saveDismissed(pick.day, null);
    setDismissedId(null);
    trackEvent(EventType.QUICK_START_RESTORED);
  }, [pick]);

  return { enabled, pick, view, upcoming, choose, dismiss, restore };
};

// Both mounted cards ask at once on load; share that one request.
let recentInFlight: Promise<number[]> | null = null;
const fetchRecentNoteTypes = (): Promise<number[]> => {
  if (!recentInFlight) {
    recentInFlight = getRecentNoteTypes()
      .then((response) => response.data.note_types ?? [])
      .finally(() => {
        recentInFlight = null;
      });
  }
  return recentInFlight;
};

export type SessionTemplateState = ReturnType<typeof useSessionTemplate>;

/**
 * The template a Quick Start will use, named before the clinician presses it.
 * See ``resolveSessionTemplate`` for the order of evidence.
 */
export const useSessionTemplate = () => {
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const { isCreateModalVisible, isAITemplateModalVisible } = useAppSelector((state) => state.appState);
  const noteTypes = React.useMemo(() => nudgeNoteTypesFor(userInfo), [userInfo]);

  // Templates change under a card that stays mounted all day: edited or deleted in My templates,
  // added from the drawer, unshared by an admin elsewhere. Refetch the list when either closes and
  // when the window regains focus, so a template that is gone stops being offered (and sent).
  const [templatesVersion, setTemplatesVersion] = React.useState(0);
  const editorsOpen = isCreateModalVisible || isAITemplateModalVisible;
  const wasEditorOpen = React.useRef(editorsOpen);
  React.useEffect(() => {
    if (wasEditorOpen.current && !editorsOpen) setTemplatesVersion((v) => v + 1);
    wasEditorOpen.current = editorsOpen;
  }, [editorsOpen]);
  React.useEffect(() => {
    let last = Date.now();
    const onFocus = () => {
      if (Date.now() - last < TEMPLATE_FOCUS_REFRESH_MS) return;
      last = Date.now();
      setTemplatesVersion((v) => v + 1);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const { groups, options, customNotesLoaded, loadGallery } = useTemplateOptions({
    noteTypes,
    galleryOnMount: false,
    refreshKey: templatesVersion,
  });
  const galleryLoaded = useAppSelector((state) => state.appState.templates.length > 0);
  const [recent, setRecent] = React.useState<number[] | null>(null);
  const [stored, setStored] = React.useState<number | null>(() => readStoredTemplateChoice());
  const [chosen, setChosen] = React.useState<number | string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetchRecentNoteTypes()
      .then((noteTypes) => {
        if (!cancelled) setRecent(noteTypes);
      })
      .catch((error) => {
        console.error("Could not load recent templates:", error);
        if (!cancelled) setRecent([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The drawer may have just remembered a different template; read it again when it closes. A pick
  // there is newer than one made on the card, so it replaces the card's pick too.
  const storedRef = React.useRef(stored);
  React.useEffect(() => {
    storedRef.current = stored;
  }, [stored]);
  React.useEffect(() => {
    if (isCreateModalVisible) return;
    const latest = readStoredTemplateChoice();
    if (latest !== storedRef.current) setChosen(null);
    setStored(latest);
  }, [isCreateModalVisible]);

  const template = React.useMemo(
    () => resolveSessionTemplate({ options, chosen, stored, recent }),
    [options, chosen, stored, recent],
  );

  const choose = React.useCallback(
    (option: TemplateOption) => {
      rememberTemplateChoice(option.value, Boolean(option.is_template));
      setStored(readStoredTemplateChoice());
      setChosen(option.value);
      trackEvent(EventType.QUICK_START_TEMPLATE_CHANGED, {
        group: option.group,
        had_template: template !== null,
        from_source: template?.source ?? null,
      });
    },
    [template],
  );

  return {
    template,
    /** False until both the picker's options and the recent history have loaded. */
    ready: customNotesLoaded && recent !== null,
    groups,
    options,
    choose,
    /** The gallery is only fetched once the picker is about to show it. */
    loadGallery: galleryLoaded ? () => {} : loadGallery,
  };
};
